// components/common/Switch.tsx
import React, { useRef, useEffect, useState } from "react";

interface SwitchOption {
  label: string;
  value: string;
}

interface SwitchProps {
  label?: string;
  data: SwitchOption[];
  value: string;
  onChange: (value: string) => void;
  className?: string;
  activeColor?: string; // e.g., "bg-primary", "bg-success"
}

export default function Switch({
  label,
  data,
  value,
  onChange,
  className = "",
  activeColor = "bg-primary",
}: SwitchProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [indicatorStyle, setIndicatorStyle] = useState({
    width: 0,
    left: 0,
  });
  const [isReady, setIsReady] = useState(false);

  // Calculate indicator position
  useEffect(() => {
    const updateIndicator = () => {
      if (!containerRef.current) return;

      const activeIndex = data.findIndex((opt) => opt.value === value);
      const buttons = containerRef.current.querySelectorAll("button");
      const activeButton = buttons[activeIndex] as HTMLElement;

      if (activeButton) {
        setIndicatorStyle({
          width: activeButton.offsetWidth,
          left: activeButton.offsetLeft,
        });
        setIsReady(true);
      }
    };

    // Small delay to ensure DOM is rendered
    const timer = setTimeout(updateIndicator, 0);

    window.addEventListener("resize", updateIndicator);
    return () => {
      clearTimeout(timer);
      window.removeEventListener("resize", updateIndicator);
    };
  }, [value, data]);

  return (
    <div className={`d-flex flex-column gap-2 ${className}`}>
      {label && (
        <label className="form-label fw-medium text-muted small mb-1">
          {label}
        </label>
      )}

      <div
        ref={containerRef}
        className="position-relative d-inline-flex bg-light rounded-pill p-1"
        style={{ width: "fit-content" }}
      >
        {/* ✨ Sliding Indicator */}
        <div
          className={`position-absolute ${activeColor} rounded-pill shadow-sm`}
          style={{
            width: isReady ? indicatorStyle.width : 0,
            left: isReady ? indicatorStyle.left : 0,
            top: "4px",
            bottom: "4px",
            transition: "all 0.3s cubic-bezier(0.23, 1, 0.32, 1)",
            opacity: isReady ? 1 : 0,
            zIndex: 0,
          }}
        />

        {/* Options */}
        {data.map((option) => {
          const isActive = value === option.value;
          return (
            <button
              key={option.value}
              onClick={() => onChange(option.value)}
              className={`
                position-relative z-1 px-4 py-2 rounded-pill fw-medium 
                border-0 bg-transparent 
                transition-colors duration-200
                ${isActive ? "text-white" : "text-muted hover-text-dark"}
              `}
              type="button"
            >
              {option.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
